import axios from "axios";
import { NetworkEndpoints } from "@/configs/NetworkEndpoints";

export interface LoginCredentials {
    email: string;
    password: string;
}

export interface RegisterData {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phoneNumber?: string;
    isMarketingEnabled?: boolean;
}

export interface AuthTokens {
    idToken: string;
    refreshToken: string;
    expiresIn: number;
}

// Backend invoice information type (snake_case)
interface InvoiceInformationResponse {
    company_name?: string;
    address1?: string;
    address2?: string;
    zipcode?: string;
    city?: string;
    country?: string;
    phone?: string;
    chamber_of_commerce?: string;
    tax_number?: string;
}

// Backend response type (snake_case)
interface UserResponse {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    slug: string;
    is_marketing_enabled?: boolean;
    invoice_information?: InvoiceInformationResponse;
}

// Frontend invoice information type (camelCase)
export interface InvoiceInformation {
    companyName?: string;
    address1?: string;
    address2?: string;
    zipcode?: string;
    city?: string;
    country?: string;
    phone?: string;
    chamberOfCommerce?: string;
    taxNumber?: string;
}

// Frontend User type (camelCase)
export interface User {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    slug: string;
    isMarketingEnabled?: boolean;
    invoiceInformation?: InvoiceInformation;
}

// Update user request type
export interface UpdateUserData {
    firstName?: string;
    lastName?: string;
    isMarketingEnabled?: boolean;
    invoiceInformation?: InvoiceInformation;
}

class AuthService {
    private readonly baseUrl = NetworkEndpoints.accountService;

    async register(
        data: RegisterData,
        redirectUrl?: string
    ): Promise<{ success: boolean; error?: string }> {
        try {
            const response = await axios.post(`${this.baseUrl}/register`, {
                type: "basic",
                fields: {
                    email: data.email,
                    password: data.password,
                    first_name: data.firstName,
                    last_name: data.lastName,
                    phone_number: data.phoneNumber || "",
                    date_of_birth: "1990-01-01", // Default DOB for now
                    is_marketing_enabled: data.isMarketingEnabled || true,
                },
                ...(redirectUrl ? { redirectUrl } : {}),
            });

            if (response.status === 200) {
                return { success: true };
            }

            return {
                success: false,
                error: response.data || "Registration failed",
            };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data || "Registration failed",
            };
        }
    }

    async login(credentials: LoginCredentials): Promise<AuthTokens | null> {
        try {
            const response = await axios.post(`${this.baseUrl}/login`, {
                type: "basic",
                fields: {
                    email: credentials.email,
                    password: credentials.password,
                },
                platform: "web",
            });

            if (response.status === 200) {
                return response.data;
            }

            return null;
        } catch (error) {
            return null;
        }
    }

    async verifyAndLogin(oobCode: string): Promise<AuthTokens | null> {
        try {
            const response = await axios.post(
                `${this.baseUrl}/verify-and-login`,
                {
                    oobCode,
                    platform: "web",
                }
            );

            if (response.status === 200) {
                return response.data;
            }

            return null;
        } catch (error) {
            return null;
        }
    }

    async refresh(refreshToken: string): Promise<AuthTokens | null> {
        try {
            const response = await axios.post(`${this.baseUrl}/refresh`, {
                refreshToken,
                platform: "web",
            });

            if (response.status === 200) {
                return response.data;
            }

            return null;
        } catch (error) {
            return null;
        }
    }

    async sendPasswordReset(
        email: string,
        redirectUrl?: string
    ): Promise<boolean> {
        try {
            const response = await axios.post(
                `${this.baseUrl}/password-reset`,
                {
                    email,
                    platform: "web",
                    ...(redirectUrl ? { redirectUrl } : {}),
                }
            );

            return response.status === 200;
        } catch (error) {
            return false;
        }
    }

    async getCurrentUser(idToken: string): Promise<User | null> {
        try {
            const response = await axios.get<UserResponse>(
                `${this.baseUrl}/user`,
                {
                    headers: {
                        Authorization: idToken, // Account service expects bare token, not "Bearer token"
                    },
                }
            );

            if (response.status === 200) {
                const userData = response.data;
                // Map snake_case to camelCase
                return {
                    id: userData.id,
                    email: userData.email,
                    firstName: userData.first_name,
                    lastName: userData.last_name,
                    slug: userData.slug,
                    isMarketingEnabled: userData.is_marketing_enabled,
                    invoiceInformation: userData.invoice_information
                        ? {
                              companyName:
                                  userData.invoice_information.company_name,
                              address1: userData.invoice_information.address1,
                              address2: userData.invoice_information.address2,
                              zipcode: userData.invoice_information.zipcode,
                              city: userData.invoice_information.city,
                              country: userData.invoice_information.country,
                              phone: userData.invoice_information.phone,
                              chamberOfCommerce:
                                  userData.invoice_information
                                      .chamber_of_commerce,
                              taxNumber:
                                  userData.invoice_information.tax_number,
                          }
                        : undefined,
                };
            }

            return null;
        } catch (error) {
            return null;
        }
    }

    // eslint-disable-next-line sonarjs/cognitive-complexity
    async updateUser(
        idToken: string,
        data: UpdateUserData
    ): Promise<User | null> {
        try {
            const requestBody: Record<string, any> = {};

            if (data.firstName !== undefined) {
                requestBody.first_name = data.firstName;
            }
            if (data.lastName !== undefined) {
                requestBody.last_name = data.lastName;
            }
            if (data.isMarketingEnabled !== undefined) {
                requestBody.is_marketing_enabled = data.isMarketingEnabled;
            }
            if (data.invoiceInformation !== undefined) {
                const invoiceInfo: Record<string, string | undefined> = {};

                if (data.invoiceInformation.companyName !== undefined) {
                    invoiceInfo.company_name =
                        data.invoiceInformation.companyName;
                }
                if (data.invoiceInformation.address1 !== undefined) {
                    invoiceInfo.address1 = data.invoiceInformation.address1;
                }
                if (data.invoiceInformation.address2 !== undefined) {
                    invoiceInfo.address2 = data.invoiceInformation.address2;
                }
                if (data.invoiceInformation.zipcode !== undefined) {
                    invoiceInfo.zipcode = data.invoiceInformation.zipcode;
                }
                if (data.invoiceInformation.city !== undefined) {
                    invoiceInfo.city = data.invoiceInformation.city;
                }
                if (data.invoiceInformation.country !== undefined) {
                    invoiceInfo.country = data.invoiceInformation.country;
                }
                if (data.invoiceInformation.phone !== undefined) {
                    invoiceInfo.phone = data.invoiceInformation.phone;
                }
                if (data.invoiceInformation.chamberOfCommerce !== undefined) {
                    invoiceInfo.chamber_of_commerce =
                        data.invoiceInformation.chamberOfCommerce;
                }
                if (data.invoiceInformation.taxNumber !== undefined) {
                    invoiceInfo.tax_number = data.invoiceInformation.taxNumber;
                }

                requestBody.invoice_information = invoiceInfo;
            }

            const response = await axios.put<UserResponse>(
                `${this.baseUrl}/user`,
                requestBody,
                {
                    headers: {
                        Authorization: idToken,
                    },
                }
            );

            if (response.status === 200) {
                const userData = response.data;
                // Map snake_case to camelCase
                return {
                    id: userData.id,
                    email: userData.email,
                    firstName: userData.first_name,
                    lastName: userData.last_name,
                    slug: userData.slug,
                    isMarketingEnabled: userData.is_marketing_enabled,
                    invoiceInformation: userData.invoice_information
                        ? {
                              companyName:
                                  userData.invoice_information.company_name,
                              address1: userData.invoice_information.address1,
                              address2: userData.invoice_information.address2,
                              zipcode: userData.invoice_information.zipcode,
                              city: userData.invoice_information.city,
                              country: userData.invoice_information.country,
                              phone: userData.invoice_information.phone,
                              chamberOfCommerce:
                                  userData.invoice_information
                                      .chamber_of_commerce,
                              taxNumber:
                                  userData.invoice_information.tax_number,
                          }
                        : undefined,
                };
            }

            return null;
        } catch (error) {
            return null;
        }
    }
}

export const authService = new AuthService();
